import { SeoContentData } from '@components/global/SeoContentComponent/SeoContentComponent';
import {
  CategoryNavCarouselSliceData,
  HeroBannerSlice,
  MarkdownCopySlice,
} from './slices';
import { AlgonomyProduct } from '@components/global/Recommendations/Loader';

export enum Animation {
  none = 'none',
  imageScrollFade = 'image scroll fade',
  imageAnfTextScrollFade = 'text and image scroll fade',
}

export enum ImageVariations {
  image = 'image',
  video = 'video',
}

export enum CtaImportance {
  primary = 'primary',
  secondary = 'secondary',
  tertiary = 'tertiary',
  unStyled = 'unstyled',
}

export type HeroImage = {
  alt?: string;
  dimensions: {
    height: number;
    width: number;
  };
  url: string;
};

export type Copy = {
  text?: string;
  type?: string;
}[];

export enum BrandedStoreSliceType {
  hero = 'hero',
  heroBanner = 'hero_banner',
  mainHero = 'main_hero',
  learnStrip = 'learn_strip',
  promoTiles = 'promo_tiles',
  activityNavigationGrid = 'activity_navigation_grid',
  collectionAssortment = 'collection_assortment',
  photoGrid = 'photo_grid',
  brandValues = 'brand_values',
  heroPLP = 'hero_plp',
  imageComponent = 'images',
  featuredBox = 'featured_box',
  productGridWithTooltips = 'product_grid_with_tooltips',
  featuredBestSellers = 'best_sellers',
  markdownCopy = 'markdowncopy',
  copyWithCta = 'copy_with_cta',
  categoryNavCarousel = 'category_nav_carousel',
  bestSellerRecommendations = 'best_seller_recommendations',
  ctaStack = 'cta_stack',
  categoryGrid = 'category_grid',
  imageWithText = 'image_with_text',
  valueProps = 'value_props',
  seoContentComponent = 'seo_content_component',
}

export interface HeroSlice {
  id?: string;
  locale?: string;
  slice_type?: BrandedStoreSliceType.hero;
  sliceIndex?: number;
  primary: {
    id?: string;
    animation?:
      | Animation.none
      | Animation.imageScrollFade
      | Animation.imageAnfTextScrollFade;
    image_variations: ImageVariations.image | ImageVariations.video;
    logo?: HeroImage;
    video_id?: string;
    video_height?: number;
    video_width?: number;
    image?: HeroImage;
    image_mobile?: HeroImage;
    mobile_image?: HeroImage;
    title?: string;
    copy: Copy;
    cta_label?: string;
    cta_importance: 'primary' | 'secondary' | 'tertiary' | 'unstyled';
    cta_link?: string;
    text_below_image: boolean;
  };
  brandedStoreCoopAttribute?: string | null;
}

export interface PhotoGridSlice {
  id?: string;
  locale?: string;
  slice_type?: BrandedStoreSliceType.photoGrid;
  primary?: {
    id?: string;
    cta_label?: string;
    cta_url?: string;
    cta_importance?:
      | CtaImportance.primary
      | CtaImportance.secondary
      | CtaImportance.tertiary
      | CtaImportance.unStyled;
  };
  items?: {
    copy: Copy;
    format: ImageVariations.image | ImageVariations.video;
    image: HeroImage;
    video_id?: string;
    video_height: number;
    video_width: number;
    position: string;
    copy_indent: string;
    copy_position: string;
  }[];
  brandedStoreCoopAttribute?: string | null;
  sliceIndex?: number;
}

export interface BrandValueProp {
  id?: string;
  image_url?: string;
  title?: string;
  copy: Copy;
  image: HeroImage;
  cta_text?: string;
  cta_url?: string;
  image_link?: string;
}

export interface HomepageValueProp {
  value_prop_icon: HeroImage;
  value_prop_title?: string;
  value_prop_content?: string;
  value_prop_link?: string;
}

export interface BrandValueSlice {
  id?: string;
  slice_type?: BrandedStoreSliceType.brandValues;
  primary?: {
    id?: string;
    title?: string;
    number_of_cards?: string;
  };
  items?: BrandValueProp[];
}

export interface CollectionAssortmentSlice {
  id?: string;
  locale?: string;
  slice_type?: BrandedStoreSliceType.collectionAssortment;
  items?: {
    icon: HeroImage;
    icon_description?: string;
  }[];
  primary: {
    id?: string;
    content_left?: boolean;
    copy: Copy;
    collection_cta_importance:
      | CtaImportance.primary
      | CtaImportance.secondary
      | CtaImportance.tertiary;
    collection_cta_label?: string;
    collection_cta_link?: string;
    collection_logo: HeroImage;
    collection_image: HeroImage;
    collection_path: string;
    collection_title?: string;
    quantity_of_products?: string;
  };
  products?: AlgonomyProduct[];
  brandedStoreCoopAttribute?: string | null;
  sliceIndex?: number;
}

export interface HeroPLPSlice {
  id?: string;
  slice_type?: BrandedStoreSliceType.heroPLP;
  primary: {
    id?: string;
    logo?: HeroImage;
    background_image: HeroImage;
    title: string;
    copy: Copy;
    image_variations: ImageVariations.image | ImageVariations.video;
    video_id?: string;
  };
}

export interface ImageComponentSlice {
  id?: string;
  slice_type?: BrandedStoreSliceType.imageComponent;
  primary?: {
    id?: string;
    title?: string;
    number_of_cards?: string;
    copy_position?: string;
    copy?: Copy;
    cta_title?: string;
    cta_link?: string;
    no_wrap: boolean;
    caption?: string;
  };
  items?: {
    title?: string;
    copy?: Copy;
    image: HeroImage;
    mobile_image: HeroImage;
    aspect_ratio?: string;
  }[];
  brandedStoreCoopAttribute?: string | null;
  sliceIndex?: number;
}

export interface FeaturedBoxSlice {
  id?: string;
  primary: {
    id?: string;
    background_image: HeroImage;
    copy: Copy;
    cta_label?: string;
    cta_url?: string;
    overlay_image: HeroImage;
    title?: string;
  };
  slice_type?: BrandedStoreSliceType.featuredBox;
  locale?: string;
  brandedStoreCoopAttribute?: string | null;
  sliceIndex?: number;
}

export type PrismicImage =
  | {
      id: string;
      alt: string | null;
      dimensions: {
        height: number;
        width: number;
      };
      url: string | null;
    }
  | Record<string, never>;

export interface ProductGridWithToolTips {
  id?: string;
  slice_type?: BrandedStoreSliceType.productGridWithTooltips;
  primary: {
    id?: string;
    title: string | null;
    copy: Copy;
    alt_position: boolean;
    cta_importance: 'primary' | 'secondary' | 'tertiary';
    cta_text: string | null;
    cta_url: string | null;
    icon?: PrismicImage;
    social_proof_top: Copy;
    social_proof_top_size: 'small' | 'large';
    social_proof_bottom: Copy;
    social_proof_bottom_size: 'small' | 'large';
  };
  items: {
    product_image: PrismicImage;
    tooltip1_title: string | null;
    tooltip1_body: Array<any>;
    tooltip1_x: number | null;
    tooltip1_y: number | null;
    tooltip2_title: string | null;
    tooltip2_body: Array<any>;
    tooltip2_x: number | null;
    tooltip2_y: number | null;
    tooltip3_title: string | null;
    tooltip3_body: Array<any>;
    tooltip3_x: number | null;
    tooltip3_y: number | null;
  }[];
}
export interface FeaturedBestSellersSlice {
  id?: string;
  locale?: string;
  slice_type?: BrandedStoreSliceType.featuredBestSellers;
  primary: {
    title?: string;
    cta_title?: string;
    cta_link?: string;
    collection_path: string;
    number_of_products: string;
  };
  products?: AlgonomyProduct[];
  brandedStoreCoopAttribute?: string | null;
}

export interface CtaStackItem {
  id?: string;
  title: string;
  link: string;
  cta_importance: 'primary' | 'secondary' | 'tertiary' | 'unstyled';
}

export interface BestSellerRecommendationsSlice {
  id?: string;
  slice_type?: BrandedStoreSliceType.bestSellerRecommendations;
}

export interface CategoryGridItem {
  id: string;
  image: {
    url: string;
    alt: string;
  };
  link_text: string;
  url: string;
}

export interface CategoryGridSlice {
  id?: string;
  slice_type?: BrandedStoreSliceType.categoryGrid;
  primary: {
    title: string;
    trackpromos: boolean;
    mobile_scroll: boolean;
  };
  items: CategoryGridItem[];
}

export interface ImageWithTextSlice {
  id?: string;
  slice_type?: BrandedStoreSliceType.imageWithText;
  primary: {
    id?: string;
    eplp_title: string;
    eplp_copy: string;
    markdown_copy: Copy;
    eplp_cta_text: string;
    eplp_cta_link: string;
    eplp_desktop_image: PrismicImage;
    eplp_mobile_image: PrismicImage;
    text_on_right: boolean;
  };
}

export interface CtaStackSlice {
  id?: string;
  slice_type?: BrandedStoreSliceType.ctaStack;
  primary: {
    id?: string;
    title: string;
    link: string;
    cta_importance: 'primary' | 'secondary' | 'tertiary' | 'unstyled';
  };
  items: CtaStackItem[];
}

export interface SeoContentComponentSlice {
  id?: string;
  slice_type?: BrandedStoreSliceType.seoContentComponent;
  primary: SeoContentData;
}

export interface MainHeroSlice {
  id?: string;
  locale?: string;
  slice_type?: BrandedStoreSliceType.mainHero;
  sliceIndex?: number;
  primary: {
    id?: string;
    title?: string;
    image?: PrismicImage;
    image_mobile?: PrismicImage;
    cta_1_label?: string;
    cta_1_link?: string;
    cta_2_label?: string;
    cta_2_link?: string;
    info_card_description?: string;
    info_card_cta_label?: string;
    info_card_cta_link?: string;
    animation?:
      | Animation.none
      | Animation.imageScrollFade
      | Animation.imageAnfTextScrollFade;
  };
  brandedStoreCoopAttribute?: string | null;
}

export interface LearnStripItem {
  image?: PrismicImage;
  heading?: string;
  subheading?: string;
  cta_text?: string;
  /**
   * Authored by hand and free to point at a learn article, a blog post or an
   * off-site social post. The slice carries no content type alongside it,
   * since nothing downstream renders differently per source.
   */
  url?: string;
}

export interface LearnStripSlice {
  id?: string;
  locale?: string;
  slice_type?: BrandedStoreSliceType.learnStrip;
  sliceIndex?: number;
  primary: {
    id?: string;
    title?: string;
  };
  items: LearnStripItem[];
}

export interface PromoTilesItem {
  image?: PrismicImage;
  mobile_image?: PrismicImage;
  hide_gradient?: boolean;
  heading?: string;
  body?: string;
  cta_1_label?: string;
  cta_1_link?: string;
  cta_2_label?: string;
  cta_2_link?: string;
  co_op_brand?: string;
}

export interface PromoTilesSlice {
  id?: string;
  locale?: string;
  slice_type?: BrandedStoreSliceType.promoTiles;
  sliceIndex?: number;
  primary: {
    id?: string;
    title?: string;
  };
  items: PromoTilesItem[];
}

export interface ActivityNavigationGridSliceItem {
  label?: string;
  url?: string;
  image?: PrismicImage;
  cta_label?: string;
}

export interface ActivityNavigationGridSlice {
  id?: string;
  slice_type?: BrandedStoreSliceType.activityNavigationGrid;
  sliceIndex?: number;
  primary: {
    heading?: string;
    body?: string;
  };
  items: ActivityNavigationGridSliceItem[];
}

export type BrandedStoreSlice =
  | HeroSlice
  | HeroBannerSlice
  | CollectionAssortmentSlice
  | PhotoGridSlice
  | BrandValueSlice
  | HeroPLPSlice
  | FeaturedBoxSlice
  | ProductGridWithToolTips
  | FeaturedBestSellersSlice
  | ImageComponentSlice
  | CategoryNavCarouselSliceData
  | BestSellerRecommendationsSlice
  | CategoryGridSlice
  | ImageWithTextSlice
  | CtaStackSlice
  | SeoContentComponentSlice
  | MainHeroSlice
  | LearnStripSlice
  | PromoTilesSlice
  | ActivityNavigationGridSlice
  | MarkdownCopySlice;

export interface BrandedStore {
  meta_title?: string;
  meta_description?: string;
  body: BrandedStoreSlice[];
}
